import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../../../styles/LmsForm.module.css'
import closeIcon from '../../../../assets/close.png';
import { notification, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { db, storage } from '../../../../firebase-config';
import { collection, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import ButtonComponent from '../../../CssComponents/ButtonComponent/ButtonComponent';
import MediaPreviewModal from '../../../CssComponents/MediaPreviewModal/MediaPreviewModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import BatchInstallmentSettings from './BatchInstallmentSettings';

function FormBatch({ initialValues, setInitialValues, showForm, onClose }) {
    const [courseOptions, setCourseOptions] = useState([]);
    const [bannerFile, setBannerFile] = useState(null);
    const [contentFile, setContentFile] = useState(null);
    const [bannerFileList, setBannerFileList] = useState([]);
    const [contentFileList, setContentFileList] = useState([]);
    const [previewFile, setPreviewFile] = useState({ url: '', type: '' });
    const [previewVisible, setPreviewVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [courseModules, setCourseModules] = useState([]);
    const [moduleSettings, setModuleSettings] = useState({}); // Store module settings

    // this to select courses, 
    useEffect(() => {
        const fetchCourses = async () => {
            const querySnapshot = await getDocs(collection(db, 'lms_course'));
            const options = querySnapshot.docs.map(doc => ({
                label: doc.data().courseName,
                value: doc.data().courseId,
            }));
            setCourseOptions(options);
        };
        fetchCourses();
    }, []);

    // Fetch courses and their modules when batchContainingCourses changes
    useEffect(() => {
        const fetchCoursesAndModules = async () => {

            if (initialValues.batchContainingCourses?.length > 0) {
                try {
                    const coursesQuery = await getDocs(collection(db, 'lms_course'));
                    const coursesData = coursesQuery.docs
                        .filter(doc => initialValues.batchContainingCourses.includes(doc.data().courseId))
                        .map(doc => ({
                            courseId: doc.data().courseId,
                            courseName: doc.data().courseName,
                            courseContainingModules: doc.data().courseContainingModules || [],
                        }));


                    // fetching all modules
                    const modulesQuery = await getDocs(collection(db, 'lms_module'));
                    const modulesData = modulesQuery.docs.map(doc => ({
                        moduleId: doc.data().moduleId,
                        moduleName: doc.data().moduleName,
                    }));


                    //combining :  Map courses to their modules
                    const courseModulesData = coursesData.map(course => ({
                        ...course,
                        modules: modulesData.filter(module => course.courseContainingModules.includes(module.moduleId)),
                    }));

                    setCourseModules(courseModulesData);

                    // Initialize module settings if not already set
                    // Initialize module settings, merging with initialValues.moduleSettings if it exists
                    const newModuleSettings = {};
                    courseModulesData.forEach((course) => {
                        course.modules.forEach((module) => {
                            const settingId = `${course.courseId}_${module.moduleId}`;
                            const existingSettings = initialValues.moduleSettings?.[settingId] || {};
                            newModuleSettings[settingId] = {
                                settingId: existingSettings.settingId || moduleSettings[settingId]?.settingId || settingId,
                                courseId: existingSettings.courseId || moduleSettings[settingId]?.courseId || course?.courseId,
                                moduleId: existingSettings.moduleId || moduleSettings[settingId]?.moduleId || module?.moduleId,
                                moduleTrainerId: existingSettings.moduleTrainerId || moduleSettings[settingId]?.moduleTrainerId || null,
                                moduleName: existingSettings.moduleName || moduleSettings[settingId]?.moduleName || module.moduleName,
                                moduleStartDate: existingSettings.moduleStartDate || moduleSettings[settingId]?.moduleStartDate || null,
                                moduleEndDate: existingSettings.moduleEndDate || moduleSettings[settingId]?.moduleEndDate || null,
                                moduleInstallmentStatus: existingSettings.moduleInstallmentStatus ?? moduleSettings[settingId]?.moduleInstallmentStatus ?? false,
                                moduleInstallmentPrice: existingSettings.moduleInstallmentPrice ?? moduleSettings[settingId]?.moduleInstallmentPrice ?? 0,
                            };
                        });
                    });
                    setModuleSettings(newModuleSettings);
                } catch (error) {
                    console.error('Error fetching courses and modules:', error);
                    notification.error({
                        message: 'Error',
                        description: 'Failed to fetch course modules.',
                    });
                }
            } else {
                setCourseModules([]);
                setModuleSettings({});
            }
        };
        fetchCoursesAndModules();
    }, [initialValues.batchContainingCourses, initialValues.moduleSettings]);

    useEffect(() => {
        if (initialValues.batchBanner) {
            setBannerFileList([{
                uid: initialValues.batchId || '-1',
                name: 'existing_banner',
                status: 'done',
                url: initialValues.batchBanner,
            }]);
        }
        if (initialValues.batchContent) {
            setContentFileList([{
                uid: initialValues.batchId || '-2',
                name: 'existing_content',
                status: 'done',
                url: initialValues.batchContent,
            }]);
        }
        return () => {
            setBannerFileList([]);
            setContentFileList([]);
        };
    }, [initialValues.batchBanner, initialValues.batchContent, initialValues.batchId]);

    const handlePreview = useCallback(async (file) => {
        const fileUrl = file.url || file.thumbUrl;
        if (fileUrl) {
            const fileType = file.type || (file.name ? file.name.split('.').pop().toLowerCase() : '');
            let previewType = 'image';
            if (fileType.includes('video') || fileUrl.includes('.mp4') || fileUrl.includes('.webm')) {
                previewType = 'video';
            } else if (fileType.includes('pdf') || fileUrl.includes('.pdf')) {
                previewType = 'pdf';
            }
            setPreviewFile({ url: fileUrl, type: previewType });
            setPreviewVisible(true);
        }
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setInitialValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, [setInitialValues]);

    const validateFile = (file, type) => {
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (!file) return false;
        if (file.size > maxSize) {
            notification.error({
                message: 'File too large',
                description: 'File must be smaller than 100MB',
            });
            return false;
        }
        return true;
    };

    const uploadToFirebaseStorage = async (file, path) => {
        try {
            if (!validateFile(file, path)) {
                throw new Error('Invalid file');
            }
            const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Upload error:', error);
            throw new Error('Failed to upload file');
        }
    };


    // anurag todo : upgrad to setting ids

    const handleModuleSettingChange = (moduleId, field, value) => {
        setModuleSettings(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                [field]: value,
            },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        console.log("moduleSettings:", moduleSettings);

        try {
            let bannerUrl = initialValues.batchBanner || '';
            let contentUrl = initialValues.batchContent || '';
            const isUpdate = !!initialValues.batchId;

            if (bannerFile && bannerFile instanceof File) {
                bannerUrl = await uploadToFirebaseStorage(bannerFile, 'lms_batch');
            }
            if (contentFile && contentFile instanceof File) {
                contentUrl = await uploadToFirebaseStorage(contentFile, 'lms_batch');
            }

            const batchData = {
                ...initialValues,
                batchBanner: bannerUrl,
                batchContent: contentUrl,
                moduleSettings: moduleSettings, // Include module settings in batch data
                updatedAt: new Date().toISOString(),
            };

            if (isUpdate) {
                await updateDoc(doc(db, 'lms_batch', initialValues.batchId), batchData);
                notification.success({
                    message: 'Batch Updated',
                    description: 'Batch updated successfully.',
                    placement: 'topRight',
                    duration: 3,
                });
            } else {
                const docRef = await addDoc(collection(db, 'lms_batch'), batchData);
                await updateDoc(doc(db, 'lms_batch', docRef.id), {
                    batchId: docRef.id,
                    createdAt: new Date().toISOString(),
                });
                notification.success({
                    message: 'Batch Created',
                    description: 'New batch created successfully.',
                    placement: 'topRight',
                    duration: 3,
                });
            }

            onClose();
            setInitialValues({});
            setBannerFile(null);
            setContentFile(null);
            setBannerFileList([]);
            setContentFileList([]);
            setModuleSettings({});
        } catch (error) {
            console.error('Error saving batch:', error);
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to save batch. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBannerChange = ({ fileList }) => {
        setBannerFileList(fileList);
        const file = fileList.length > 0 ? fileList[0].originFileObj : null;
        setBannerFile(file);
    };

    const handleContentChange = ({ fileList }) => {
        setContentFileList(fileList);
        const file = fileList.length > 0 ? fileList[0].originFileObj : null;
        setContentFile(file);
    };

    return (
        <div className={showForm ? `${styles.modal} ${styles.displayBlock}` : `${styles.modal} ${styles.displayNone}`}>
            <div className={styles.modalMain}>
                <button className={styles.closeBtn} onClick={onClose} disabled={isLoading}>
                    <img src={closeIcon} alt="Close" />
                </button>
                <div className={styles.mainContent}>
                    <div className={styles.title}>Batch</div>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Batch Name</div>
                            <input
                                name="batchName"
                                className={styles.input}
                                value={initialValues.batchName || ''}
                                onChange={handleChange}
                                placeholder="Enter Batch Name"
                                required
                                disabled={isLoading}
                            />
                        </div>


                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Batch Description</div>
                            <ReactQuill
                                theme="snow"
                                value={initialValues.batchDescription || ''}
                                onChange={(value) => handleChange({ target: { name: 'batchDescription', value } })}
                                placeholder="Enter Batch Description"
                                readOnly={isLoading}
                            />
                        </div>

                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Batch Banner (Image)</div>
                            <Upload
                                accept="image/*"
                                listType="picture-card"
                                beforeUpload={() => false}
                                fileList={bannerFileList}
                                onChange={handleBannerChange}
                                onPreview={handlePreview}
                                showUploadList={{ showRemoveIcon: true }}
                                maxCount={1}
                                disabled={isLoading}
                            >
                                {bannerFileList.length < 1 && (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>Upload <br /> Banner</div>
                                    </div>
                                )}
                            </Upload>
                        </div>

                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Select Courses</div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select courses"
                                value={initialValues.batchContainingCourses || []}
                                onChange={(value) =>
                                    setInitialValues(prev => ({
                                        ...prev,
                                        batchContainingCourses: value,
                                    }))
                                }
                                options={courseOptions}
                            />
                        </div>

                        <BatchInstallmentSettings
                            courseModules={courseModules}
                            moduleSettings={moduleSettings}
                            handleModuleSettingChange={handleModuleSettingChange}
                            isLoading={isLoading}
                        />

                        <ButtonComponent
                            buttonText={initialValues.batchId ? 'Update Batch' : 'Save Batch'}
                            buttonType="submit"
                            disabled={isLoading}
                            loading={isLoading}
                        />
                    </form>
                </div>
            </div>

            <MediaPreviewModal
                previewVisible={previewVisible}
                setPreviewVisible={setPreviewVisible}
                previewFile={previewFile}
            />
        </div>
    );
}

export default FormBatch;